import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useJournal } from "@/hooks/useData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, NotebookPen, Trash2, Loader2 } from "lucide-react";

const MOODS = ["confident", "neutral", "cautious", "frustrated"];
const MOOD_DOT = {
  confident: "bg-[hsl(var(--profit))]",
  neutral: "bg-muted-foreground/50",
  cautious: "bg-[hsl(var(--warning))]",
  frustrated: "bg-[hsl(var(--loss))]",
};

function blankDraft() {
  return {
    entry_id: null,
    date: new Date().toISOString().slice(0, 10),
    title: "",
    content: "",
    mood: "neutral",
    tags: [],
  };
}

export default function JournalPage() {
  const { data: entries, isLoading } = useJournal();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["journal"] });

  const save = async () => {
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error("Give your entry a title and some notes");
      return;
    }
    setBusy(true);
    try {
      if (draft.entry_id) {
        await api.put(`/journal/${draft.entry_id}`, draft);
        toast.success("Entry updated");
      } else {
        await api.post("/journal", draft);
        toast.success("Entry saved");
      }
      invalidate();
      setDraft(null);
    } catch {
      toast.error("Couldn't save entry");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    toast.success("Entry deleted");
    invalidate();
    if (draft?.entry_id === id) setDraft(null);
  };

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">Trade Journal</h1>
            <p className="mt-1 text-sm text-muted-foreground">Write down the why behind your trades. Your future self will thank you.</p>
          </div>
          <Button onClick={() => setDraft(blankDraft())} data-testid="new-entry-button">
            <Plus className="mr-1.5 h-4 w-4" /> New entry
          </Button>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[340px_1fr]">
          {/* entries list */}
          <div className="space-y-2" data-testid="journal-entries-list">
            {isLoading && [...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
            {!isLoading && !entries?.length && (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed py-12 text-center">
                <NotebookPen className="h-7 w-7 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">No entries yet.</p>
                <Button variant="secondary" size="sm" onClick={() => setDraft(blankDraft())} data-testid="journal-empty-cta">
                  Write your first entry
                </Button>
              </div>
            )}
            {(entries || []).map((e) => (
              <button key={e.entry_id}
                onClick={() => setDraft({ ...e })}
                className={cn(
                  "w-full rounded-xl border bg-card p-3.5 text-left transition-all duration-150 hover:shadow-[0_6px_20px_rgba(2,6,23,0.08)]",
                  draft?.entry_id === e.entry_id && "ring-2 ring-ring"
                )}
                data-testid={`journal-entry-${e.entry_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 truncate text-sm font-medium">
                    <span className={cn("h-2 w-2 shrink-0 rounded-full", MOOD_DOT[e.mood] || MOOD_DOT.neutral)} />
                    {e.title}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{fmtDate(e.date)}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">{e.content}</p>
              </button>
            ))}
          </div>

          {/* editor */}
          <div className="rounded-2xl border bg-card p-5">
            {draft ? (
              <div className="space-y-4" data-testid="journal-editor">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Title</Label>
                    <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="e.g. Overtraded the London open" data-testid="journal-title-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                      data-testid="journal-date-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Mood</Label>
                  <Select value={draft.mood || "neutral"} onValueChange={(v) => setDraft({ ...draft, mood: v })}>
                    <SelectTrigger className="w-44" data-testid="journal-mood-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MOODS.map((m) => (
                        <SelectItem key={m} value={m}>
                          <span className="flex items-center gap-2 capitalize">
                            <span className={cn("h-2 w-2 rounded-full", MOOD_DOT[m])} /> {m}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea rows={10} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                    placeholder="What happened? What did you do well? What would you change?" data-testid="journal-content-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={save} disabled={busy} data-testid="journal-save-button">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {draft.entry_id ? "Update entry" : "Save entry"}
                  </Button>
                  <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
                  {draft.entry_id && (
                    <Button variant="ghost" className="ml-auto text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => remove(draft.entry_id)} data-testid="journal-delete-button">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 text-center">
                <NotebookPen className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">Select an entry to read or edit it, or start a new one.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
